import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferencePseudoElements() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔊 CSS Aural (Speech) Properties</h1>

      {/* Intro */}
      <p className="tutorial-intro" style={{ marginBottom: "30px" }}>
        <strong>CSS Aural properties</strong> (also called <b>Speech CSS</b>)
        control how web content is spoken by speech synthesizers. These were
        introduced in the <b>CSS2 Speech Module</b> and are mostly used in
        assistive technologies such as screen readers.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Works inside <code>@media speech</code> to control audio rendering.
          </li>
          <li>Controls pitch, volume, pauses, and speed of spoken content.</li>
          <li>
            Useful for <strong>accessibility</strong> and visually impaired
            users.
          </li>
          <li>
            Support is limited in browsers, but common in screen readers and
            speech systems.
          </li>
        </ul>
      </div>

      {/* Properties Table */}
      <div
        style={{
          background: "linear-gradient(135deg, #ecfdf5, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          overflowX: "auto",
        }}
      >
        <h2 style={{ color: "#065f46" }}>🎛️ Aural Properties Reference</h2>
        <table className="tutorial-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Description</th>
              <th>Values</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>azimuth</td>
              <td>Sets where the sound should come from</td>
              <td>
                angle, left-side, far-left, left, center-left, center,
                center-right, right, far-right, right-side, behind, leftwards,
                rightwards
              </td>
            </tr>
            <tr>
              <td>cue</td>
              <td>Sets cue sounds in one declaration</td>
              <td>cue-before, cue-after</td>
            </tr>
            <tr>
              <td>cue-before / cue-after</td>
              <td>Sound before/after speaking content</td>
              <td>none, url</td>
            </tr>
            <tr>
              <td>elevation</td>
              <td>Vertical direction of sound</td>
              <td>angle, below, level, above, higher, lower</td>
            </tr>
            <tr>
              <td>pause / pause-before / pause-after</td>
              <td>Sets pauses around speech</td>
              <td>time, %</td>
            </tr>
            <tr>
              <td>pitch</td>
              <td>Controls pitch of the voice</td>
              <td>frequency, x-low, low, medium, high, x-high</td>
            </tr>
            <tr>
              <td>pitch-range</td>
              <td>Variation in pitch (monotone/animated)</td>
              <td>number</td>
            </tr>
            <tr>
              <td>play-during</td>
              <td>Background sound while speaking</td>
              <td>auto, none, url, mix, repeat</td>
            </tr>
            <tr>
              <td>richness</td>
              <td>Rich or thin voice quality</td>
              <td>number</td>
            </tr>
            <tr>
              <td>speak</td>
              <td>Whether content should be spoken</td>
              <td>normal, none, spell-out</td>
            </tr>
            <tr>
              <td>speak-header</td>
              <td>Handling of table headers</td>
              <td>always, once</td>
            </tr>
            <tr>
              <td>speak-numeral</td>
              <td>How numbers are spoken</td>
              <td>digits, continuous</td>
            </tr>
            <tr>
              <td>speak-punctuation</td>
              <td>How punctuation is spoken</td>
              <td>none, code</td>
            </tr>
            <tr>
              <td>speech-rate</td>
              <td>Speed of speech</td>
              <td>
                number, x-slow, slow, medium, fast, x-fast, faster, slower
              </td>
            </tr>
            <tr>
              <td>stress</td>
              <td>Specifies emphasis in the voice</td>
              <td>number</td>
            </tr>
            <tr>
              <td>voice-family</td>
              <td>Defines which voice to use</td>
              <td>specific-voice, generic-voice</td>
            </tr>
            <tr>
              <td>volume</td>
              <td>Loudness of the voice</td>
              <td>number, %, silent, x-soft, soft, medium, loud, x-loud</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Example */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h3 style={{ color: "#1e3a8a" }}>📘 Example (with @media speech)</h3>
        <CodeCopyBlock
          language="css"
          code={`@media speech {
  h1 {
    voice-family: male;
    volume: loud;
    pitch: high;
    speak: normal;
  }

  p {
    pause-before: 300ms;
    pause-after: 200ms;
    speech-rate: medium;
  }
}`}
        />
        <p style={{ marginTop: "10px" }}>
          👉 Defines how <code>h1</code> and <code>p</code> elements are read
          aloud.
        </p>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #dcfce7, #bbf7d0)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Try <code>@media speech</code> with different <b>pitch</b>,{" "}
            <b>rate</b>, and <b>volume</b> values.
          </li>
          <li>
            Use with assistive technology or screen readers for accessibility
            testing.
          </li>
          <li>
            Combine with <code>ARIA</code> attributes for more semantic
            accessibility.
          </li>
        </ul>
      </div>
    </div>
  );
}
